// Global Instructions Rule Applied!
// Read shapes for the fund Chart of Accounts tab
// (view__fund_fund_chart_of_accounts) and the GL Accounts / Chart Templates
// configuration panels (view__gl_account_admin, view__chart_templates).
//
// Row schemas are .strict(), so every key the RPC emits must appear here or
// validation fails at runtime (REACT_APP_VALIDATE_REQUEST_DATA).
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

// One row per live catalogue account. acc_chart_of_accounts__id is null when
// the fund does not hold the account (prog__active false) - the grid still
// lists it so it can be switched on.
export const FundChartAccountRowSchema = z
  .object({
    acc_chart_of_accounts__id: z.string().nullish(),
    cfg_acc_gl_accounts__id: z.string(),
    acc_gl_accounts__account_code: z.string().nullish(),
    acc_gl_accounts__account_name: z.string(),
    cfg_acc_account_types__label: z.string().nullish(),
    prog__active: z.boolean(),
    prog__journal_count: z.number(),
    prog__balance: z.number(),
    prog__balance_fmt: z.string().nullish()
  })
  .strict()

export type FundChartAccountRow = z.infer<typeof FundChartAccountRowSchema>

// One row per live accounting item with its effective account.
// prog__source mirrors ft__coa_for_item's resolution order; prog__resolves is
// false when that function would RAISE, i.e. booking this item fails.
export const FundChartItemRowSchema = z
  .object({
    cfg_acc_items__id: z.string(),
    cfg_acc_items__label: z.string(),
    cfg_acc_items__call_component: z.boolean(),
    prog__effective_gl_account_id: z.string().nullish(),
    prog__source: z.enum(['override', 'global', 'none']),
    prog__resolves: z.boolean(),
    prog__journal_count_open: z.number(),
    prog__journal_count_closed: z.number()
  })
  .strict()

export type FundChartItemRow = z.infer<typeof FundChartItemRowSchema>

export interface ChartGlAccountOption {
  value: string
  label: string
  code?: string | null
  type?: string | null
}

export interface ChartSourceFundOption {
  value: string
  label: string
  account_count?: number
}

export interface ChartTemplateOption {
  value: string
  label: string
  account_count?: number
}

// Configuration panel: catalogue rows.
export const GlAccountAdminRowSchema = z
  .object({
    cfg_acc_gl_accounts__id: z.string(),
    acc_gl_accounts__account_code: z.string().nullish(),
    acc_gl_accounts__account_name: z.string(),
    cfg_acc_account_types__id: z.string().nullish(),
    cfg_acc_account_types__label: z.string().nullish(),
    prog__fund_count: z.number(),
    prog__journal_count: z.number(),
    prog__template_count: z.number()
  })
  .strict()

export type GlAccountAdminRow = z.infer<typeof GlAccountAdminRowSchema>

// Configuration panel: the global item -> account default mapping, with the
// funds that follow it (and would therefore be affected by a move).
export const GlItemDefaultRowSchema = z
  .object({
    cfg_acc_items__id: z.string(),
    cfg_acc_items__label: z.string(),
    cfg_acc_items__call_component: z.boolean(),
    prog__default_gl_account_id: z.string().nullish(),
    prog__override_fund_count: z.number(),
    prog__affected_funds: z.array(
      z.object({ funds__id: z.string(), funds__name: z.string().nullish() }).passthrough()
    )
  })
  .strict()

export type GlItemDefaultRow = z.infer<typeof GlItemDefaultRowSchema>

// Template detail passes through: nested account / item-map arrays follow the
// module convention of validating flat row arrays only.
export interface ChartTemplateAccount {
  cfg_acc_gl_accounts__id: string
  acc_gl_accounts__account_code?: string | null
  acc_gl_accounts__account_name?: string | null
  cfg_acc_account_types__label?: string | null
}

export interface ChartTemplateItem {
  cfg_acc_items__id: string
  cfg_acc_items__label?: string | null
  cfg_acc_gl_accounts__id: string
}

export interface ChartTemplate {
  cfg_acc_chart_templates__id: string
  cfg_acc_chart_templates__name: string
  cfg_acc_chart_templates__description?: string | null
  funds__id?: string | null
  funds__name?: string | null
  prog__account_count: number
  prog__item_count: number
  prog__accounts: ChartTemplateAccount[]
  prog__items: ChartTemplateItem[]
}

export function validateFundChartAccountRows(data: unknown): FundChartAccountRow[] {
  try {
    return validateWithZodSchema(FundChartAccountRowSchema, data, 'fund chart account row', { isArray: true })
  } catch (error) {
    throw new Error(`Fund chart account validation failed: ${error}`)
  }
}

export function validateFundChartItemRows(data: unknown): FundChartItemRow[] {
  try {
    return validateWithZodSchema(FundChartItemRowSchema, data, 'fund chart item row', { isArray: true })
  } catch (error) {
    throw new Error(`Fund chart item validation failed: ${error}`)
  }
}

export function validateGlAccountAdminRows(data: unknown): GlAccountAdminRow[] {
  try {
    return validateWithZodSchema(GlAccountAdminRowSchema, data, 'GL account admin row', { isArray: true })
  } catch (error) {
    throw new Error(`GL account admin validation failed: ${error}`)
  }
}

export function validateGlItemDefaultRows(data: unknown): GlItemDefaultRow[] {
  try {
    return validateWithZodSchema(GlItemDefaultRowSchema, data, 'GL item default row', { isArray: true })
  } catch (error) {
    throw new Error(`GL item default validation failed: ${error}`)
  }
}
